/**
 * Single-elimination bracket math — pure helpers shared by the bracket UI and
 * unit tests. The same structure rules are enforced server-side in the
 * `generate_tournament_bracket` / `set_bracket_match_result` RPCs (see
 * migration 20260813000000_tournament_brackets): match `m` in round `r` feeds
 * match `floor(m/2)` in round `r+1`, side p1 when `m` is even, p2 when odd.
 */

/** The subset of a bracket match these helpers need. */
export interface MatchLike {
  round: number
  /** 0-based slot within the round. */
  position: number
  p1UserId: string | null
  p2UserId: string | null
  winnerUserId: string | null
  isBye: boolean
}

/** Smallest power of two >= n (minimum 2): the number of bracket slots. */
export function bracketSize(n: number): number {
  let size = 2
  while (size < n) size *= 2
  return size
}

/** Number of rounds for a bracket of `size` slots (log2). */
export function roundCount(size: number): number {
  return Math.round(Math.log2(size))
}

/** Players who skip round 1 when n isn't a power of two. */
export function byeCount(n: number): number {
  return bracketSize(n) - n
}

/** Where the winner of (round, position) advances to. */
export function nextSlot(
  round: number,
  position: number
): { round: number; position: number; slot: 'p1' | 'p2' } {
  return {
    round: round + 1,
    position: Math.floor(position / 2),
    slot: position % 2 === 0 ? 'p1' : 'p2',
  }
}

/** Display label for a round, counting back from the final. */
export function roundLabel(round: number, totalRounds: number): string {
  const fromEnd = totalRounds - round
  if (fromEnd === 0) return 'Final'
  if (fromEnd === 1) return 'Semifinals'
  if (fromEnd === 2) return 'Quarterfinals'
  return `Round ${round}`
}

export interface BracketProgress {
  /** Lowest round with an undecided match; totalRounds when complete. */
  currentRound: number
  /** Undecided matches in that round. */
  pendingInRound: number
  complete: boolean
}

/** Progress summary for the manage card (byes count as decided). */
export function bracketProgress(
  matches: MatchLike[],
  totalRounds: number
): BracketProgress {
  for (let round = 1; round <= totalRounds; round++) {
    const pending = matches.filter(
      (m) => m.round === round && !m.winnerUserId
    ).length
    if (pending > 0) return { currentRound: round, pendingInRound: pending, complete: false }
  }
  return { currentRound: totalRounds, pendingInRound: 0, complete: true }
}

/**
 * Whether the organizer may record/change this match's result right now: both
 * players present, not a bye, and the downstream match (where the winner
 * advances) undecided. Mirrors the RPC's rule so the UI never offers an edit
 * the server would reject.
 */
export function isMatchEditable(
  match: MatchLike,
  matches: MatchLike[],
  totalRounds: number
): boolean {
  if (match.isBye) return false
  if (!match.p1UserId || !match.p2UserId) return false
  if (match.round >= totalRounds) return true
  const next = nextSlot(match.round, match.position)
  const downstream = matches.find(
    (m) => m.round === next.round && m.position === next.position
  )
  return !downstream?.winnerUserId
}
